import React from 'react';
import { ResponsiveContainer, Tooltip, XAxis, BarChart, Bar } from 'recharts';
import { safeNum, safeArray, formatInt } from '../../../utils/gpsFormUtils';
import { NUMERIC_DISTINCT_MAX, NUMERIC_GATE } from '../../../constants/gpsForms';
import { VIOLET } from './constants';
import ExplorerNotice from './ExplorerNotice';
import GPSTopGraph from '../GPSTopGraph';

const HETEROGENEOUS_NOTE =
  'Données trop hétérogènes pour un indicateur fiable — distribution affichée à la place.';

const NumericStatCard = ({ label, value }) => (
  <div className="rounded-xl border border-gray-200 bg-gray-50/50 dark:border-gray-700 dark:bg-white/[0.03] p-4">
    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</p>
    <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{value}</p>
  </div>
);

const NumericView = ({ breakdown }) => {
  const current =
    breakdown?.current && typeof breakdown.current === 'object' ? breakdown.current : {};
  const distinct = safeArray(current.distinct);
  const histogram = safeArray(current.histogram);
  const parseRatio = safeNum(current.parseRatio, 1);
  const unparseableCount = safeNum(current.unparseableCount);

  const exclusionNote =
    unparseableCount > 0
      ? `${formatInt(unparseableCount)} réponse(s) non numérique(s) ignorée(s) dans le calcul.`
      : null;

  const frVal = (v) =>
    Number.isFinite(Number(v))
      ? Number(Number(v).toFixed(2)).toLocaleString('fr-FR')
      : '—';

  // parseRatio < 0.9 → hide averages, show distribution + heterogeneous note.
  if (parseRatio < NUMERIC_GATE) {
    const data = distinct.map((d) => ({
      label: String(d?.value ?? ''),
      value: safeNum(d?.count),
    }));
    return (
      <div data-testid="explorer-numeric-distribution">
        {data.length > 0 ? (
          <GPSTopGraph title="Distribution" data={data} height="280px" />
        ) : (
          <ExplorerNotice>Aucune valeur numérique exploitable.</ExplorerNotice>
        )}
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">{HETEROGENEOUS_NOTE}</p>
        {exclusionNote && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{exclusionNote}</p>
        )}
      </div>
    );
  }

  // distinct ≤ 7 → categorical distribution bars (not a mean).
  if (distinct.length > 0 && distinct.length <= NUMERIC_DISTINCT_MAX) {
    const data = distinct.map((d) => ({
      label: String(d?.value ?? ''),
      value: safeNum(d?.count),
    }));
    return (
      <div data-testid="explorer-numeric-categorical">
        <GPSTopGraph title="Distribution" data={data} height="280px" />
        {exclusionNote && (
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">{exclusionNote}</p>
        )}
      </div>
    );
  }

  // 4 stat tiles in individual bordered cards + violet histogram.
  // Backend may return histogram:[] but distinct is populated — build bars from distinct.
  const histogramData =
    histogram.length > 0
      ? histogram.map((h) => ({
          label: `${safeNum(h?.binStart)}–${safeNum(h?.binEnd)}`,
          count: safeNum(h?.count),
        }))
      : distinct.map((d) => ({
          label: String(d?.value ?? ''),
          count: safeNum(d?.count),
        }));

  return (
    <div data-testid="explorer-numeric-tiles">
      {/* 4 bordered stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <NumericStatCard label="Moyenne" value={frVal(current.mean)} />
        <NumericStatCard label="Médiane" value={frVal(current.median)} />
        <NumericStatCard label="Min" value={frVal(current.min)} />
        <NumericStatCard label="Max" value={frVal(current.max)} />
      </div>

      {/* Histogram — from distinct when histogram is empty */}
      {histogramData.length > 0 && (
        <div className="h-[220px] mt-6">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={histogramData} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                style={{ fontSize: '11px' }}
                tick={{ fill: '#9CA3AF' }}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '12px',
                }}
              />
              <Bar dataKey="count" fill={VIOLET} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {exclusionNote && (
        <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">{exclusionNote}</p>
      )}
    </div>
  );
};

export default NumericView;
